/**
 * QuickTaskModal Component (Self-Task / "My Reminder")
 * Quick modal for creating personal tasks with file attachments
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/overlays';
import { Stack, Group, Button, Text } from '@/components/ui';
import { TextInput, Select, DatePicker } from '@/components/ui/inputs';
import CompactHtmlEditor from '@/components/ui/inputs/CompactHtmlEditor';
import FileUpload from '@/components/advanced/media/FileUpload';
import { toast } from '@/components/ui/providers/AlertProvider';
import { IconPlus } from '@tabler/icons-react';
import {
  useAuth,
  useTaskService,
  useLocationService,
  useDepartmentService,
  useFileService,
  useTaskPortalAttachmentService,
  useTaskPortalAssignmentService,
} from '@actschurch/ccms-sdk';
import { useTaskStore } from '@/stores/taskStore';
import { TASK_STATUS, TASK_PRIORITY } from '../utils/taskConstants';

export function QuickTaskModal() {
  const queryClient = useQueryClient();
  const taskService = useTaskService();
  const locationService = useLocationService();
  const departmentService = useDepartmentService();
  const fileService = useFileService();
  const portalAttachmentService = useTaskPortalAttachmentService();
  const portalAssignmentService = useTaskPortalAssignmentService();
  const auth = useAuth();
  const currentUserId = auth?.state?.user?.userId;

  const opened = useTaskStore((s) => s.quickTaskModalOpen);
  const setOpened = useTaskStore((s) => s.setQuickTaskModalOpen);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [files, setFiles] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentService.getAll(),
    staleTime: 30 * 60 * 1000,
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationService.getAll(),
    staleTime: 30 * 60 * 1000,
  });

  const departmentOptions = departments.map((d: any) => ({
    value: d.departmentID?.toString() || d.id?.toString(),
    label: d.name || d.departmentName,
  }));

  const locationOptions = locations.map((l: any) => ({
    value: l.locationID?.toString() || l.id?.toString(),
    label: l.name || l.locationName,
  }));

  /**
   * Submit flow:
   * 1. Validate required fields (Title, Department, Location, Due Date).
   * 2. Create the task and capture the new taskID from the response.
   * 3. Assign the creator to their own task. A quick task is a self-task, but
   *    `POST /api/tasks` only writes the Task row — it creates a TaskAssignment
   *    solely when the admin `AutoAssignOnCreate` setting is on (it defaults to
   *    off). Every "assigned to me" surface (board, My Tasks, Assigned Tasks,
   *    dashboard counts) filters on TaskAssignment rows, so without this the
   *    quick task only ever showed under "Created by Me".
   * 4. Upload each picked file against the new task via the File API
   *    (`domain=Task`, `recordId={taskId}`) so the backend creates the
   *    matching TaskAttachment rows. Previously the modal only forwarded
   *    raw `File` objects to the create payload with empty fileURL values,
   *    so nothing got persisted to S3.
   * 5. Toast partial-failure messaging if any file fails — the task still
   *    succeeds, but the user knows which uploads didn't go through.
   */
  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.warning('Please enter a title');
      return;
    }
    if (!departmentId) {
      toast.warning('Please select a department');
      return;
    }
    if (!locationId) {
      toast.warning('Please select a location');
      return;
    }
    if (!dueDate) {
      toast.warning('Please select a due date');
      return;
    }

    setSubmitting(true);
    try {
      const created: any = await taskService.create({
        title: title.trim(),
        description: description || null,
        departmentID: Number(departmentId),
        locationID: Number(locationId),
        dueDate: dueDate.toISOString(),
        statusID: TASK_STATUS.UNSEEN,
        priorityID: TASK_PRIORITY.LOW,
        projectID: 0,
      } as any);

      const newTaskId = created?.taskID ?? created?.TaskID ?? created?.id;

      // Self-assign — this is what turns the new row into a real self-task:
      // it creates the TaskAssignment the "assigned to me" queries filter on,
      // and it's also what makes `isSelfTask` (creator + assignee) resolve
      // true, so the "Self" badge renders. The portal endpoint is creator-only,
      // which the caller always is here. Failure is non-fatal — the task exists
      // either way — but the user is told it won't show on their board.
      // Prefer the CreatedBy the backend stamped from the JWT over the id held
      // in auth state — the assignment has to carry the exact same id the
      // assigned-tasks filter compares against.
      const assigneeId = created?.createdBy ?? currentUserId;
      let assignFailed = false;
      if (newTaskId && assigneeId) {
        try {
          await portalAssignmentService.assignUser(Number(newTaskId), assigneeId);
        } catch (err) {
          console.error('[QuickTaskModal] self-assign failed', err);
          assignFailed = true;
        }
      } else if (newTaskId) {
        // No resolvable user id (unauthenticated/stale session) — same outcome.
        assignFailed = true;
      }

      // Upload each picked file to the new task. We do this sequentially
      // rather than in parallel to keep the progress predictable and avoid
      // overwhelming the proxy fallback when S3 CORS isn't configured.
      //
      // The upload itself only creates a `FileRecord` in the FileManagement
      // service — TaskManagement's `TaskAttachment` table is in a different
      // DB and there is no event bridge between them. So after each successful
      // upload we follow up with `createAttachment` against the task-portal
      // endpoint to register the file as a TaskAttachment; this is what makes
      // the file appear in the task's Files tab on subsequent reads.
      let uploadFailures = 0;
      if (newTaskId && files.length > 0) {
        for (const f of files) {
          // FileUpload component wraps File in { file, ... }; support both
          // that shape and a bare File for safety.
          const rawFile: File | undefined = f?.file instanceof File ? f.file : (f instanceof File ? f : undefined);
          if (!rawFile) continue;
          try {
            const uploaded = await fileService.uploadFileAuto(rawFile, {
              domain: 'Task',
              recordId: String(newTaskId),
            });
            await portalAttachmentService.createAttachment(Number(newTaskId), {
              fileName: rawFile.name,
              fileURL: uploaded?.url ?? '',
              fileType: rawFile.type || undefined,
            });
          } catch (err) {
            console.error('[QuickTaskModal] file upload failed', err);
            uploadFailures += 1;
          }
        }
      }

      if (assignFailed) {
        toast.warning(
          "Task created, but it couldn't be assigned to you — it won't appear on your board or under \"Assigned to Me\".",
        );
      } else if (uploadFailures > 0) {
        toast.warning(
          `Task created, but ${uploadFailures} of ${files.length} attachment(s) failed to upload.`,
        );
      } else {
        toast.success('Quick task created successfully');
      }
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      handleClose();
    } catch (err) {
      console.error('[QuickTaskModal] create failed', err);
      toast.error('Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setDepartmentId(null);
    setLocationId(null);
    setDueDate(null);
    setFiles([]);
    setOpened(false);
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Quick Task"
      size="lg"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Create a quick task for yourself to track your work.
        </Text>

        <TextInput
          label="Title"
          placeholder="What do you need to do?"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          required
        />

        <Group grow>
          <Select
            label="Department"
            placeholder="Select department"
            data={departmentOptions}
            value={departmentId}
            onChange={setDepartmentId}
            searchable
            clearable
            required
          />
          <Select
            label="Location"
            placeholder="Select location"
            data={locationOptions}
            value={locationId}
            onChange={setLocationId}
            searchable
            clearable
            required
          />
        </Group>

        <DatePicker
          label="Due Date"
          placeholder="Select due date"
          value={dueDate}
          onChange={setDueDate}
          minDate={new Date()}
          required
        />

        <div>
          <Text size="sm" fw={500} mb={4}>Details</Text>
          <CompactHtmlEditor
            value={description}
            onChange={setDescription}
            placeholder="Add more details..."
          />
        </div>

        <div>
          <Text size="sm" fw={500} mb={4}>Attachments</Text>
          <FileUpload
            value={files}
            onChange={setFiles}
            multiple
            variant="compact"
            maxFiles={5}
            maxSize={10 * 1024 * 1024}
            description="Drag files here or click to upload (max 5 files, 10MB each)"
          />
        </div>

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            gradientVariant="primary"
            leftSection={<IconPlus size={16} />}
            onClick={handleSubmit}
            loading={submitting}
          >
            Create Task
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
